import { Dispatch, Fragment, SetStateAction, useMemo, useState } from 'react';
import {
	DocumentIdentity,
	DuplicateFile,
	ProcessingFile,
	UploadEntityDetails,
	UploadStatusStep,
} from '@dealstack/frontend/shared/components/Documents/types';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Container, FileDropperContainerOuter, StepTabContainer, StickyFooter } from './styled';
import { AddFileStep } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/AddFileStep';
import { ValidateFileStep } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/ValidateFileStep';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { StepTab } from '@dealstack/frontend/shared/components/StepTab/StepTab';
import { ManageFileAssociationStep } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/ManageFileAssociationStep';
import { Footer } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/Footer';
import { UploadCompleteStep } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/UploadCompleteStep';
import { AnimatedStyledIcon } from '@dealstack/frontend/shared/components/StepTab/styled';
import { useMantineTheme } from '@mantine/core';
import { FindRelatedFilesRes } from '@dealstack/shared/lib/generated';
import { DocumentLinkTarget } from '@dealstack/frontend/shared/dataExchange/services/shared/types';
import { FileWithClassifier } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/AddFileStep/types';

interface Props {
	setIsUploadActive: Dispatch<SetStateAction<boolean>>;
	dealId: string;
	/** Absent for deal-scoped uploads from the Document Library. */
	target?: DocumentLinkTarget;
	uploadEntityDetails?: UploadEntityDetails[];
	/**
	 * lets the host open a document that already holds the file being uploaded. Optional —
	 * the deal directory has nowhere to send the user, so it simply omits the affordance.
	 */
	onViewExistingDocument?: (existingDocument: DocumentIdentity) => void;
}

export const UploadDocuments = ({
	setIsUploadActive,
	dealId,
	target,
	uploadEntityDetails,
	onViewExistingDocument,
}: Props) => {
	const theme = useMantineTheme();
	const tCapManage = useTranslationCapManage();
	const [canButtonProceed, setCanButtonProceed] = useState(false);
	const [uploadFileData, setUploadFileData] = useState<FileWithClassifier[]>([]);
	const [uploadStatusStep, setUploadStatusStep] = useState<UploadStatusStep>(
		UploadStatusStep.addFiles
	);
	const [isUploadingFiles, setIsUploadingFiles] = useState<boolean>(false);
	const [localDuplicateFiles, setLocalDuplicateFiles] = useState<{
		filesWithDuplicates: DuplicateFile[];
		totalDuplicates: number;
	}>({
		filesWithDuplicates: [],
		totalDuplicates: 0,
	});
	const [processingFiles, setProcessingFiles] = useState<ProcessingFile[]>([]);
	const [relatedFilesInDeal, setRelatedFilesInDeal] = useState<FindRelatedFilesRes[]>();

	const [nbrFilesUploaded, setNbrFilesUploaded] = useState<number>(0);
	const [nbrFiles, setNbrFiles] = useState<number>(0);

	// payload for duplicate files in a deal
	const clearDocumentState = () => {
		setUploadFileData([]);
		setProcessingFiles([]);
		setLocalDuplicateFiles({
			filesWithDuplicates: [],
			totalDuplicates: 0,
		});
		setRelatedFilesInDeal([]);
		setCanButtonProceed(false);
	};

	const uploadProgress = useMemo(() => {
		if (!nbrFiles || !nbrFilesUploaded) {
			return 0;
		}

		return (nbrFilesUploaded / nbrFiles) * 100;
	}, [nbrFiles, nbrFilesUploaded]);

	const stepTabs = useMemo(() => {
		return [
			{
				header: tCapManage('document.upload_state.step_tab.one.header'),
				subHeader: tCapManage('document.upload_state.step_tab.one.sub_header'),
				isActive: uploadStatusStep === UploadStatusStep.addFiles,
			},
			{
				header: tCapManage('document.upload_state.step_tab.two.header'),
				subHeader: tCapManage('document.upload_state.step_tab.two.sub_header'),
				isActive: uploadStatusStep === UploadStatusStep.validateUploads,
			},
			{
				header: tCapManage('document.upload_state.step_tab.three.header'),
				subHeader: tCapManage('document.upload_state.step_tab.three.sub_header'),
				isActive: uploadStatusStep === UploadStatusStep.fileDealDuplicate,
			},
		];
	}, [tCapManage, uploadStatusStep]);

	/* Step four upload the documents and links */
	if (uploadStatusStep === UploadStatusStep.uploadComplete) {
		return (
			<UploadCompleteStep
				setIsUploadActive={setIsUploadActive}
				uploadFileData={uploadFileData}
				isUploadingFiles={isUploadingFiles}
				localDuplicateFiles={localDuplicateFiles}
				relatedFilesInDeal={relatedFilesInDeal}
				uploadProgress={uploadProgress}
				clearDocumentState={clearDocumentState}
			/>
		);
	}

	return (
		<Container>
			<StepTabContainer>
				{stepTabs.map((step, index) => (
					<Fragment key={`${step.header}_${index}`}>
						<StepTabContainer key={`${step.header}_${index}`}>
							<StepTab
								icon={
									index < stepTabs.findIndex((s) => s.isActive) ? (
										<AnimatedStyledIcon
											variant="tick-circle"
											size="1.25rem"
											fillStroke={true}
											color={theme.colors.teal[8]}
										/>
									) : undefined
								}
								header={step.header}
								isActive={step.isActive}
								subHeader={step.subHeader}
							/>
						</StepTabContainer>
						{index < stepTabs.length - 1 && (
							<Icon variant="arrow-right-v3" style={{ opacity: 0.5 }} />
						)}
					</Fragment>
				))}
			</StepTabContainer>
			<FileDropperContainerOuter>
				{/* Step one Add your files */}
				{uploadStatusStep === UploadStatusStep.addFiles && (
					<AddFileStep
						dealId={dealId}
						setCanButtonProceed={setCanButtonProceed}
						uploadFileData={uploadFileData}
						setUploadFileData={setUploadFileData}
					/>
				)}
				{/* Step two check for local duplicates */}
				{uploadStatusStep === UploadStatusStep.validateUploads && (
					<ValidateFileStep
						localDuplicateFiles={localDuplicateFiles}
						setLocalDuplicateFiles={setLocalDuplicateFiles}
						setCanButtonProceed={setCanButtonProceed}
					/>
				)}
				{/* Step three check for deal duplicates */}
				{uploadStatusStep === UploadStatusStep.fileDealDuplicate && (
					<ManageFileAssociationStep
						relatedFilesInDeal={relatedFilesInDeal}
						setRelatedFilesInDeal={setRelatedFilesInDeal}
						uploadEntityDetails={uploadEntityDetails}
						onViewExistingDocument={onViewExistingDocument}
					/>
				)}
				<StickyFooter>
					<Footer
						clearDocumentState={clearDocumentState}
						setNbrFilesUploaded={setNbrFilesUploaded}
						setNbrFiles={setNbrFiles}
						setIsUploadActive={setIsUploadActive}
						dealId={dealId}
						target={target}
						canButtonProceed={canButtonProceed}
						setCanButtonProceed={setCanButtonProceed}
						uploadFileData={uploadFileData}
						setUploadFileData={setUploadFileData}
						uploadStatusStep={uploadStatusStep}
						setUploadStatusStep={setUploadStatusStep}
						setIsUploadingFiles={setIsUploadingFiles}
						localDuplicateFiles={localDuplicateFiles}
						setLocalDuplicateFiles={setLocalDuplicateFiles}
						processingFiles={processingFiles}
						setProcessingFiles={setProcessingFiles}
						relatedFilesInDeal={relatedFilesInDeal}
						setRelatedFilesInDeal={setRelatedFilesInDeal}
					/>
				</StickyFooter>
			</FileDropperContainerOuter>
		</Container>
	);
};
